<template>
  <section class="px-4 py-12 md:py-16">
    <div class="max-w-3xl mx-auto w-full flex flex-col items-center text-center">
      <h2>{{ t('landing.preview.title') }}</h2>
      <p class="text-lg md:text-xl text-neutral-200 max-w-2xl">{{ t('landing.preview.body') }}</p>

      <div class="w-full mt-6 rounded-2xl bg-neutral-700 p-4 md:p-6 text-left">
        <div class="overflow-x-auto">
          <div class="min-w-[32rem] flex flex-col gap-4">
            <div class="flex flex-row gap-2">
              <span
                v-for="period in PERIODS"
                :key="period"
                class="px-3 py-1 rounded-lg border text-sm"
                :class="period === '30'
                  ? 'bg-primary text-black border-primary'
                  : 'text-neutral-300 border-neutral-500'"
              >
                {{ t(`landing.preview.period${period}`) }}
              </span>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                v-for="card in CARDS"
                :key="card.key"
                :label="t(`landing.preview.${card.key}`)"
                :value="card.value"
              />
            </div>

            <div class="rounded-lg bg-neutral-800 border border-neutral-700 p-4">
              <h3 class="text-sm font-semibold text-neutral-300 mt-0 mb-2">
                {{ t('landing.preview.listensOverTime') }}
              </h3>
              <svg
                class="w-full h-40"
                :viewBox="`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`"
                preserveAspectRatio="none"
                role="presentation"
                aria-hidden="true"
              >
                <polygon :points="areaPoints" fill="#33cccc30" />
                <polyline
                  :points="linePoints"
                  fill="none"
                  stroke="#33cccc"
                  stroke-width="2"
                  vector-effect="non-scaling-stroke"
                />
              </svg>
            </div>

            <table class="w-full text-left">
              <thead>
                <tr class="border-b border-neutral-500">
                  <th class="p-2 text-sm text-neutral-300">{{ t('landing.preview.episode') }}</th>
                  <th class="p-2 text-sm text-neutral-300 text-right">{{ t('landing.preview.listens') }}</th>
                  <th class="p-2 text-sm text-neutral-300 text-right">{{ t('landing.preview.uniqueListeners') }}</th>
                  <th class="p-2 text-sm text-neutral-300 text-right">{{ t('landing.preview.listeningTime') }}</th>
                  <th class="p-2 text-sm text-neutral-300 text-right">{{ t('landing.preview.completionRate') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="episode in EPISODES" :key="episode.title" class="border-b border-neutral-700">
                  <td class="p-2">{{ episode.title }}</td>
                  <td class="p-2 text-right">{{ episode.listens }}</td>
                  <td class="p-2 text-right">{{ episode.listeners }}</td>
                  <td class="p-2 text-right">{{ episode.time }}</td>
                  <td class="p-2 text-right">{{ episode.completion }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import StatCard from '@/components/stats/StatCard.vue'

const { t } = useI18n()

const PERIODS = ['7', '30', '90', 'All'] as const

// Pre-formatted strings rather than numbers: StatCard runs toLocaleString() on
// numbers, and Node's locale need not match the browser's during hydration.
const CARDS = [
  { key: 'listens', value: '12’480' },
  { key: 'uniqueListeners', value: '8’130' },
  { key: 'listeningTime', value: '2’914 h' },
  { key: 'completionRate', value: '71.4%' },
]

const EPISODES = [
  { title: 'Folge 128 — Was die Stadt nachts wach hält', listens: '1’840', listeners: '1’207', time: '412 h', completion: '78.2%' },
  { title: 'Folge 127 — Zu Besuch in der Werkstatt', listens: '1’512', listeners: '1’038', time: '331 h', completion: '74.9%' },
  { title: 'Folge 126 — Drei Fragen an die Nachbarschaft', listens: '1’274', listeners: '902', time: '268 h', completion: '69.5%' },
  { title: 'Folge 125 — Der lange Weg zum ersten Album', listens: '1’096', listeners: '781', time: '245 h', completion: '72.1%' },
]

const CHART_WIDTH = 300
const CHART_HEIGHT = 100
const SERIES = [38, 45, 41, 58, 52, 67, 71, 64, 79, 86, 80, 94, 102, 110]
const SCALE_MAX = 120

const points = SERIES.map((value, index) => {
  const x = (index * CHART_WIDTH) / (SERIES.length - 1)
  const y = CHART_HEIGHT - (value / SCALE_MAX) * (CHART_HEIGHT - 6)
  return `${x.toFixed(2)},${y.toFixed(2)}`
})

const linePoints = points.join(' ')
const areaPoints = `0,${CHART_HEIGHT} ${linePoints} ${CHART_WIDTH},${CHART_HEIGHT}`
</script>
